import styles from "./SuggestionsMenu.module.scss";
import { useDispatch } from "react-redux";
import { ArrowRightIcon, CloseIcon, RecentIcon, SavedViewIcon } from "../Icons";
import classNames from "classnames";
import { SavedView } from "../../../../../../../types";
import { Link } from "react-router-dom";
import { removeFromRecentSearches } from "../../../../../../../redux/SingleDataset/actions";
import { useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { VLChatIcon } from "../../../../../../../assets/icons/VLChatIcon";
import { useComposerRuntime } from "@assistant-ui/react";

type SuggestionsMenuProps = {
  query: string;
  datasetId: string;
  className?: string;
  onRecentSearchClick: (s: string) => void;
  onSavedViewClick: (view: SavedView) => void;
  onAskChatClick: () => void;
  savedViews: SavedView[];
  recentSearches: string[];
  isChatEnabled: boolean;
};

export const SuggestionsMenu = ({
  query,
  datasetId,
  className,
  onSavedViewClick,
  onRecentSearchClick,
  onAskChatClick,
  recentSearches,
  savedViews,
  isChatEnabled,
}: SuggestionsMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className={classNames(styles.SuggestionsMenu, className)} ref={ref}>
      {query ? (
        isChatEnabled && (
          <AskChat onAskChatClick={onAskChatClick} query={query} />
        )
      ) : (
        <>
          {Boolean(recentSearches.length) && (
            <RecentSearches
              searches={recentSearches}
              datasetId={datasetId}
              onRecentSearchClick={onRecentSearchClick}
            />
          )}
          {Boolean(savedViews.length) && (
            <SavedViews
              views={savedViews.slice(0, 5)}
              datasetId={datasetId}
              onSavedViewClick={onSavedViewClick}
            />
          )}
        </>
      )}
    </div>
  );
};

const RecentSearches = ({
  searches,
  datasetId,
  onRecentSearchClick,
}: Pick<SuggestionsMenuProps, "onRecentSearchClick" | "datasetId"> & {
  searches: string[];
}) => {
  const dispatch = useDispatch();
  const handleRemoveClick = (e: any, search: string) => {
    e.stopPropagation();
    dispatch(removeFromRecentSearches(datasetId, search) as any);
  };
  return (
    <div className={styles.section}>
      <div className={styles.label}>Recent</div>
      {searches.map((search) => (
        <div
          className={styles.item}
          key={search}
          onClick={() => onRecentSearchClick(search)}
        >
          <RecentIcon />
          <div className={styles.text}>{search}</div>
          <CloseIcon onClick={(e) => handleRemoveClick(e, search)} />
        </div>
      ))}
    </div>
  );
};

const SavedViews = ({
  views,
  datasetId,
  onSavedViewClick,
}: Pick<SuggestionsMenuProps, "onSavedViewClick" | "datasetId"> & {
  views: SavedView[];
}) => {
  return (
    <div className={styles.section}>
      <div className={styles.label}>Saved Views</div>
      {views.map((view) => (
        <div
          className={styles.item}
          key={view.id}
          onClick={() => onSavedViewClick(view)}
        >
          <SavedViewIcon />
          <div className={styles.text}>{view.name}</div>
        </div>
      ))}
      <Link className={styles.seeAllViews} to={`/dataset/${datasetId}/views`}>
        See All <ArrowRightIcon />
      </Link>
    </div>
  );
};

const preventDefault = (fn: Function) => (e: KeyboardEvent) => {
  e.preventDefault();
  fn();
};

const AskChat = ({
  onAskChatClick,
  query,
}: {
  query: string;
  onAskChatClick: () => void;
}) => {
  const [isActive, setIsActive] = useState(false);
  const composerRuntime = useComposerRuntime();

  const handleQuery = () => {
    // Use assistant-ui's composer to send the message
    composerRuntime.setText(query);
    composerRuntime.send();
    onAskChatClick();
  };
  useHotkeys(
    "ArrowDown",
    preventDefault(() => setIsActive(true)),
    { enableOnFormTags: true },
  );
  useHotkeys(
    "ArrowUp",
    preventDefault(() => setIsActive(false)),
    { enableOnFormTags: true },
  );
  useHotkeys(
    "Enter",
    (e) => {
      if (isActive) {
        e.preventDefault();
        handleQuery();
      }
    },
    {
      enableOnFormTags: true,
    },
  );
  return (
    <div className={styles.section}>
      <div
        className={classNames(styles.askChat, { [styles.active]: isActive })}
        onClick={handleQuery}
      >
        <VLChatIcon />
        <div>Ask VL Chat</div>
      </div>
    </div>
  );
};
